import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Dialog from "@mui/material/Dialog";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { SelectBlockForm } from "./SelectBlockForm";
import { EditBlockForm } from "./EditBlockForm";
import { getDefaultBlockProps } from "../config/blockTypes";
import type { PlatformBlock, PlatformBlockType } from "../types";

interface NewBlockDialogProps {
  onClose: () => void;
  onCompleted: (block: PlatformBlock) => void;
  platformId?: string;
  queries?: Record<string, any>;
}

/**
 * Диалог создания нового блока:
 * - Сначала показывает выбор типа блока
 * - Затем показывает форму редактирования блока
 */
export const NewBlockDialog: React.FC<NewBlockDialogProps> = ({
  onClose,
  onCompleted,
  platformId,
  queries,
}) => {
  const { t } = useTranslation();
  const [selectedBlock, setSelectedBlock] = useState<PlatformBlock | null>(
    null
  );

  const onSetBlock = (
    blockType: PlatformBlockType,
    viewType?: "CAROUSEL" | "GRID"
  ) => {
    setSelectedBlock({
      component: blockType,
      props: {
        ...getDefaultBlockProps(blockType),
        ...(viewType ? { view: viewType } : {}),
      },
      visibilityConditions: {
        userType: "ALL",
        countries: {
          type: "ALL",
          countryCodes: [],
        },
        applicationPlatforms: {
          type: "ALL",
          platforms: [],
        },
      },
    });
  };

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  if (!selectedBlock) {
    return (
      <Dialog
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreen}
        open={true}
        onClose={onClose}
      >
        <SelectBlockForm onClose={onClose} onSelectBlock={onSetBlock} />
      </Dialog>
    );
  }

  return (
    <Dialog
      key="edit-block-form"
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="body"
    >
      <EditBlockForm
        title={t("platformBlocks.dialog.newTitle")}
        onClose={onClose}
        onSubmit={onCompleted}
        onCancel={() => setSelectedBlock(null)}
        initialValues={selectedBlock}
        platformId={platformId}
        queries={queries}
        mode="create"
        cancelText={t("platformBlocks.dialog.back")}
        submitText={t("platformBlocks.dialog.create")}
      />
    </Dialog>
  );
};
